import { Slider as BaseSlider } from "@base-ui/react/slider";
import classNames from "classnames";

import styles from "./Slider.module.css";

interface SliderBaseProps {
  value: number;
  onValueChange: (value: number) => void;
  min: number;
  max: number;
  step: number;
  disabled?: boolean;
  className?: string;
  /**
   * The value as a person reads it, for assistive technology. Defaults to
   * {@link valueLabel} when that is given.
   */
  getAriaValueText?: (value: number) => string;
  /**
   * Shows the value in a bubble over the thumb while it is hovered, dragged
   * or focused, as this formats it. Omit for no bubble.
   */
  valueLabel?: (value: number) => string;
}

/** A slider has no visible label of its own, so it needs an accessible name. */
export type SliderProps = SliderBaseProps &
  (
    | { "aria-label": string; "aria-labelledby"?: string }
    | { "aria-label"?: string; "aria-labelledby": string }
  );

/**
 * One number picked along a range by dragging or with the arrow keys,
 * optionally showing the value over the thumb while it moves.
 *
 * @example
 * ```tsx
 * <Slider
 *   value={scale}
 *   onValueChange={setScale}
 *   min={0.1}
 *   max={1}
 *   step={0.05}
 *   aria-label="Scale"
 *   valueLabel={(scale) => `${Math.round(scale * 100)}%`}
 * />
 * ```
 */
export function Slider({
  value,
  onValueChange,
  min,
  max,
  step,
  disabled = false,
  className,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
  getAriaValueText,
  valueLabel,
}: SliderProps) {
  const ariaValueText = getAriaValueText ?? valueLabel;
  return (
    <BaseSlider.Root
      className={classNames(styles.root, className)}
      value={value}
      onValueChange={(next) => onValueChange(next)}
      min={min}
      max={max}
      step={step}
      disabled={disabled}
    >
      <BaseSlider.Control className={styles.control}>
        <BaseSlider.Track className={styles.track}>
          <BaseSlider.Indicator className={styles.indicator} />
          <BaseSlider.Thumb
            className={styles.thumb}
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledBy}
            getAriaValueText={
              ariaValueText === undefined
                ? undefined
                : (_formatted, thumbValue) => ariaValueText(thumbValue)
            }
          >
            {valueLabel !== undefined && (
              <span
                className={styles.valueLabel}
                aria-hidden="true"
                data-testid="slider-value-label"
              >
                {valueLabel(value)}
              </span>
            )}
          </BaseSlider.Thumb>
        </BaseSlider.Track>
      </BaseSlider.Control>
    </BaseSlider.Root>
  );
}
